import { UseQueryResult, useQuery } from "@tanstack/react-query";
import { omit } from "lodash";
import * as React from "react";

import { buildPlanCode } from "helpers/subscription";
import { ApiManager, IRequestResult } from "services/api";
// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-cycle
import { ISubscriptionPreviewParams } from "services/api/subscription/subscription-api";
import { ISubscription } from "store/entities/subscriptions";

import { useDebounce } from "./use-debounce";

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/naming-convention
interface SubscriptionChangePreviewParams {
  current: ISubscription;
  update: ISubscription;
  couponCode?: string;
  countryCode?: string;
  zipCode?: string;
  children?: React.ReactNode | ((data: UseQueryResult<SubscriptionChangePreviewResponse, Error>) => React.ReactNode);
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/naming-convention
export interface SubscriptionChangePreviewPayload {
  planCost: number;
  dueNow: number;
  accountBalance: number;
  taxInCents: number;
  taxRate: number;
  taxRegion: number;
  children: React.ReactNode;
}

export type SubscriptionChangePreviewResponse = IRequestResult<SubscriptionChangePreviewPayload>;

export const WRONG_COUPON_ERROR_MESSAGE = "Invalid request params";

const WRONG_COUPON_ERRORS = [
  "Coupon invalid",
  "External service error",
  "Unknown validation error", // TODO: Remove when task done: https://livechatinc.atlassian.net/browse/HD-4027
];

export async function fetchSubscriptionPreview(
  id: string,
  params: ISubscriptionPreviewParams
): Promise<SubscriptionChangePreviewResponse> {
  let response = await ApiManager.subscriptionApi.preview(id, params);

  // retry request without coupon if it's not applicable
  if (
    response.error &&
    (response.error.error === WRONG_COUPON_ERROR_MESSAGE || WRONG_COUPON_ERRORS.includes(response.error.error.message))
  ) {
    const paramsWithoutCoupon = omit(params, "couponCode");
    response = await ApiManager.subscriptionApi.preview(id, paramsWithoutCoupon);
  }

  return response;
}

export const useSubscriptionChangeCalculation = ({
  current,
  update,
  couponCode,
  countryCode,
  zipCode,
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
}: SubscriptionChangePreviewParams) => {
  const debouncedCountry = useDebounce<string | undefined>(countryCode);
  const debouncedZipCode = useDebounce<string | undefined>(zipCode);

  const query = useQuery<SubscriptionChangePreviewResponse, Error>(
    [
      "subscriptionPreviewChange",
      {
        quantity: update.agents,
        planCode: buildPlanCode(update.plan, update.billingCycle),
        ...(update.couponCode || couponCode ? { couponCode: update.couponCode || couponCode } : {}),
        ...(debouncedCountry ? { country: debouncedCountry } : {}),
        ...(debouncedZipCode ? { postalCode: debouncedZipCode } : {}),
      },
    ],
    ({ queryKey: [, params] }) => fetchSubscriptionPreview(current.id, params as ISubscriptionPreviewParams),
    {
      refetchOnWindowFocus: false,
    }
  );

  return query;
};

export const WithSubscriptionChange: React.FC<SubscriptionChangePreviewParams> = ({
  current,
  update,
  couponCode,
  countryCode,
  zipCode,
  children,
}) => {
  const data = useSubscriptionChangeCalculation({ current, update, couponCode, countryCode, zipCode });

  return typeof children === "function" ? <>{children(data)}</> : <>{children}</>;
};
